'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectActiveCategoryId } from '@repo/store/maps/slices/clientContext';
import { selectFilterState, selectFoundPins, selectIsInCreatorMode, selectLocationCategories, selectMapData, selectSelectedMarkerId } from '@repo/store/maps/slices/mapData';
import { getFilteredFeatures } from '@repo/store/maps/utils/mapData';
import type { MapDataCategory, MapDataLocationFeature, MapDataSubcategory } from '@repo/types/maps/map-types';
import { getFontInfoForGlyph, getIconForGlyph, getSubcategoryIconStyle } from '@repo/types/maps/map-utils';
import React, { useCallback, useMemo } from 'react';
import { useMapControls } from '../game-map/hooks/useMapControls';
import { useMapPinOperations } from '../game-map/hooks/useMapPinOperations';
interface SearchResultItemProps {
  location: MapDataLocationFeature;
  category: MapDataCategory | null;
  subcategory: MapDataSubcategory | null;
  onClick: () => void;
  isSelected: boolean;
  supportsTeleport: boolean;
}
const SearchResultItem: React.FC<SearchResultItemProps> = React.memo(t0 => {
  const $ = _c(47);
  const {
    location,
    category,
    subcategory,
    onClick,
    isSelected,
    supportsTeleport
  } = t0;
  subcategory?.icon;
  const t1 = subcategory?.icon || "";
  let t2;
  if ($[0] !== t1) {
    t2 = getFontInfoForGlyph(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const fontInfo = t2;
  let t3;
  if ($[2] !== subcategory?.icon) {
    t3 = () => getIconForGlyph(subcategory?.icon || "");
    $[2] = subcategory?.icon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  subcategory?.icon;
  let t4;
  if ($[4] !== t3) {
    t4 = t3();
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const iconChar = t4;
  let t5;
  if ($[6] !== fontInfo.fontName) {
    t5 = getSubcategoryIconStyle(fontInfo.fontName);
    $[6] = fontInfo.fontName;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const iconStyle = t5;
  let t6;
  if ($[8] !== category?.title || $[9] !== location.properties.title || $[10] !== subcategory?.title) {
    t6 = () => location.properties.title || subcategory?.title || category?.title || "";
    $[8] = category?.title;
    $[9] = location.properties.title;
    $[10] = subcategory?.title;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  subcategory?.title;
  category?.title;
  let t7;
  if ($[12] !== t6) {
    t7 = t6();
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const locationTitle = t7;
  const t8 = isSelected ? "whiteAlpha.10" : "transparent";
  let t9;
  if ($[14] !== t8) {
    t9 = css({
      padding: "8",
      justifyContent: "flex-start",
      backgroundColor: t8,
      borderRadius: "4",
      "&:hover": {
        backgroundColor: "whiteAlpha.10"
      }
    });
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = css({
      width: "[20px]",
      height: "[20px]",
      borderRadius: "full",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "2xs",
      color: "white",
      flexShrink: 0
    });
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = category?.color || "#666";
  let t12;
  if ($[17] !== iconStyle || $[18] !== t11) {
    t12 = {
      backgroundColor: t11,
      ...iconStyle
    };
    $[17] = iconStyle;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== iconChar || $[21] !== t12) {
    t13 = <div className={t10} style={t12}>{iconChar}</div>;
    $[20] = iconChar;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = css.raw({
      minWidth: "0"
    });
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = css({
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      width: "full",
      textAlign: "left"
    });
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== locationTitle) {
    t16 = <Type variant="body-s" fontWeight="medium" color="text.primary" className={t15}>{locationTitle}</Type>;
    $[25] = locationTitle;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = css.raw({
      minWidth: "0",
      width: "full"
    });
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = css({
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      flexShrink: 1
    });
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  const t19 = subcategory?.title || category?.title || "";
  let t20;
  if ($[29] !== t19) {
    t20 = <Type variant="caption" color="text.secondary" className={t18}>{t19}</Type>;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== location.properties.gameCoords || $[32] !== supportsTeleport) {
    t21 = supportsTeleport && location.properties.gameCoords && <Icon icon="sprint" className={css({
      fontSize: "[12px]",
      color: "cyan.400",
      flexShrink: 0
    })} decorative={true} />;
    $[31] = location.properties.gameCoords;
    $[32] = supportsTeleport;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== t20 || $[35] !== t21) {
    t22 = <FlexRow align="center" gap="none" className={t17}>{t20}{t21}</FlexRow>;
    $[34] = t20;
    $[35] = t21;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== t16 || $[38] !== t22) {
    t23 = <FlexColumn align="start" gap="none" width="full" className={t14}>{t16}{t22}</FlexColumn>;
    $[37] = t16;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t13 || $[41] !== t23) {
    t24 = <FlexRow align="center" gap="12" width="full">{t13}{t23}</FlexRow>;
    $[40] = t13;
    $[41] = t23;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== onClick || $[44] !== t24 || $[45] !== t9) {
    t25 = <Button variant="ghost" onClick={onClick} color="primary" size="sm" fullWidth={true} className={t9}>{t24}</Button>;
    $[43] = onClick;
    $[44] = t24;
    $[45] = t9;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  return t25;
});
SearchResultItem.displayName = 'SearchResultItem';
export const MapSearchResults: React.FC = () => {
  const filters = useAppSelector(selectFilterState);
  const mapData = useAppSelector(selectMapData);
  const locationCategories = useAppSelector(selectLocationCategories);
  const foundPins = useAppSelector(selectFoundPins);
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const activeCategoryId = useAppSelector(selectActiveCategoryId);
  const isInCreatorMode = useAppSelector(selectIsInCreatorMode);
  const {
    selectMarkerInMap
  } = useMapPinOperations();
  const supportsTeleport = mapData?.metadata.supportsTeleport || false;
  const {
    flyTo
  } = useMapControls();
  const {
    t
  } = useT(['game_map']);
  const subcategoryLookup = useMemo(() => {
    const lookup: {
      [externalId: string]: MapDataSubcategory;
    } = {};
    if (mapData?.metadata.subcategories) {
      for (const sub of mapData.metadata.subcategories) {
        lookup[sub.externalId] = sub;
      }
    }
    return lookup;
  }, [mapData?.metadata.subcategories]);
  const filteredFeatures = useMemo(() => getFilteredFeatures(mapData, filters, foundPins, locationCategories, selectedMarkerId || null, activeCategoryId, isInCreatorMode, {
    includeHiddenSubcategoriesWhenSearching: true
  }), [mapData, filters, foundPins, locationCategories, selectedMarkerId, activeCategoryId, isInCreatorMode]);
  const featuresWithMetadata = useMemo(() => filteredFeatures.map(feature => {
    const category = locationCategories?.[feature.properties.externalId] || null;
    const subcategory = subcategoryLookup[feature.properties.subcategoryExternalId] || null;
    return {
      feature,
      category,
      subcategory
    };
  }), [filteredFeatures, locationCategories, subcategoryLookup]);
  const handleLocationClick = useCallback((feature_0: MapDataLocationFeature) => {
    selectMarkerInMap(feature_0.properties.externalId, {
      selectSource: 'sidebar_search'
    });
    flyTo({
      center: feature_0.geometry.coordinates as [number, number],
      zoom: 15,
      duration: 1000
    });
  }, [selectMarkerInMap, flyTo]);
  const resultCount = filteredFeatures.length;
  const hasResults = resultCount > 0;
  if (!filters.searchTerm.trim()) {
    return null;
  }
  return <FlexColumn gap="8" width="full" className={css.raw({
    padding: '8'
  })}>
            <FlexRow align="center" justify="center" gap="8" width="full">
                <Type variant="body-s" fontWeight="medium" color="text.primary">
                    {t($ => $.search_results)}
                </Type>
                <Type variant="caption" color="text.secondary">
                    ({resultCount} {resultCount === 1 ? t($_0 => $_0.pin) : t($_1 => $_1.pins)})
                </Type>
            </FlexRow>

            {hasResults ? <FlexColumn gap="4" width="full">
                    {featuresWithMetadata.map(({
        feature: feature_1,
        category: category_0,
        subcategory: subcategory_0
      }) => <SearchResultItem supportsTeleport={supportsTeleport} key={feature_1.properties.externalId} location={feature_1} category={category_0} subcategory={subcategory_0} onClick={() => handleLocationClick(feature_1)} isSelected={selectedMarkerId === feature_1.properties.externalId} />)}
                </FlexColumn> : <FlexColumn align="center" gap="8" justify="center" width="full" className={css.raw({
      padding: '16'
    })}>
                    <Icon icon="search_off" size="lg" className={css({
        color: 'text.secondary'
      })} decorative />
                    <Type variant="body-s" color="text.primary" textAlign="center">
                        {t($_2 => $_2.no_results_found)}
                    </Type>
                    <Type variant="body-xs" color="text.secondary" textAlign="center">
                        {t($_3 => $_3.try_different_search)}
                    </Type>
                </FlexColumn>}
        </FlexColumn>;
};